import React, { useState } from "react";
import { motion } from "framer-motion";
import Footer from "../components/Footer";
import sizingGuideImage from "../assets/homme.jpg";
import textureBg from "../assets/texture-paper.jpg";
import footMeasurement from "../assets/foot.jpg";

const SizeGuide = () => {
  const [activeTab, setActiveTab] = useState("EU");
  const [footLength, setFootLength] = useState("");
  const [calculatedSize, setCalculatedSize] = useState(null);

  const sizeConversion = {
    EU: [35, 36, 37, 38, 39, 40, 41, 42, 43],
    US: [4, 5, 6, 7, 8, 9, 10, 11, 12],
    UK: [2, 3, 4, 5, 6, 7, 8, 9, 10],
    CM: [22.5, 23, 23.5, 24, 25, 25.5, 26.5, 27, 27.5],
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const calculateSize = () => {
    if (!footLength) return;

    const cm = parseFloat(footLength);
    if (isNaN(cm)) return;

    const sizes = sizeConversion.CM;
    let closestSizeIndex = 0;
    let smallestDiff = Math.abs(cm - sizes[0]);

    for (let i = 1; i < sizes.length; i++) {
      const diff = Math.abs(cm - sizes[i]);
      if (diff < smallestDiff) {
        smallestDiff = diff;
        closestSizeIndex = i;
      }
    }

    setCalculatedSize({
      EU: sizeConversion.EU[closestSizeIndex],
      US: sizeConversion.US[closestSizeIndex],
      UK: sizeConversion.UK[closestSizeIndex],
      CM: sizeConversion.CM[closestSizeIndex],
    });
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700;800;900&family=Cormorant+Garamond:wght@300;400;500;600;700&family=Inter:wght@300;400;500;600;700&display=swap');
        
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          margin: 0;
          padding: 0;
          overflow-x: hidden;
          font-family: 'Cormorant Garamond', serif;
          background: linear-gradient(135deg, #f8f5f0 0%, #f0ebe0 100%);
        }

        .size-guide-container {
          font-family: 'Cormorant Garamond', serif;
          color: #2c1810;
          margin: 0 auto;
          overflow-x: hidden;
          background: linear-gradient(135deg, #f8f5f0 0%, #f0ebe0 100%);
        }

        /* Hero Section */
        .hero-section {
          position: relative;
          height: 100vh;
          min-height: 600px;
          background-image: linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)), url('${sizingGuideImage}');
          background-size: cover;
          background-position: center;
          background-attachment: fixed;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: white;
          text-align: center;
          margin-bottom: 60px;
        }

        .hero-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.3);
          backdrop-filter: blur(5px);
        }

        .hero-content {
          position: relative;
          z-index: 2;
          padding: 0 20px;
        }

        .hero-title {
          font-size: 3.5rem;
          font-weight: 600;
          margin-bottom: 20px;
          letter-spacing: 1px;
          text-shadow: 2px 2px 8px rgba(0,0,0,0.6);
          font-family: 'Cormorant Garamond', serif;
        }

        .hero-subtitle {
          font-size: 1.3rem;
          font-style: italic;
          text-shadow: 1px 1px 4px rgba(0,0,0,0.5);
          max-width: 700px;
          margin: 0 auto;
          line-height: 1.6;
          font-family: 'Cormorant Garamond', serif;
        }

        /* Main Content */
        .main-content {
          max-width: 1000px;
          margin: 0 auto 80px auto;
          padding: 0 25px;
        }

        /* Calculator Section */
        .calculator-section {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(212, 175, 55, 0.2);
          border-radius: 20px;
          padding: 40px 35px;
          margin-bottom: 50px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
        }

        .section-title {
          font-size: 2.5rem;
          font-weight: 600;
          color: #2c1810;
          margin-bottom: 30px;
          text-align: center;
          font-family: 'Cormorant Garamond', serif;
          position: relative;
          display: inline-block;
        }

        .section-title::after {
          content: '';
          position: absolute;
          bottom: -10px;
          left: 50%;
          transform: translateX(-50%);
          width: 80px;
          height: 2px;
          background: #d4af37;
          border-radius: 1px;
        }

        .calculator-form {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 25px;
          max-width: 500px;
          margin: 0 auto;
        }

        .input-group {
          width: 100%;
        }

        .input-label {
          display: block;
          margin-bottom: 8px;
          color: #5a4a3a;
          font-size: 16px;
          font-weight: 500;
          font-family: 'Cormorant Garamond', serif;
        }

        .size-input {
          width: 100%;
          padding: 15px 18px;
          border: 1px solid rgba(212, 175, 55, 0.2);
          border-radius: 15px;
          font-size: 16px;
          color: #2c1810;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
          font-family: 'Cormorant Garamond', serif;
        }

        .size-input:focus {
          outline: none;
          border-color: #d4af37;
          background: rgba(255, 255, 255, 0.15);
          box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1);
        }

        .size-input::placeholder {
          color: #5a4a3a;
          font-family: 'Cormorant Garamond', serif;
        }

        .calculate-btn {
          padding: 15px 30px;
          background: rgba(212, 175, 55, 0.2);
          color: #2c1810;
          border: 1px solid rgba(212, 175, 55, 0.3);
          border-radius: 15px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 16px rgba(212, 175, 55, 0.2);
          font-family: 'Cormorant Garamond', serif;
          text-transform: uppercase;
        }

        .calculate-btn:hover {
          background: rgba(212, 175, 55, 0.3);
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(212, 175, 55, 0.3);
        }

        .result-card {
          width: 100%;
          max-width: 500px;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(212, 175, 55, 0.2);
          border-radius: 20px;
          padding: 25px;
          text-align: center;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
        }

        .result-title {
          font-size: 18px;
          color: #2c1810;
          margin-bottom: 15px;
          font-weight: 600;
          font-family: 'Cormorant Garamond', serif;
        }

        .result-grid {
          display: flex;
          justify-content: center;
          gap: 25px;
          flex-wrap: wrap;
        }

        .result-item {
          text-align: center;
        }

        .result-label {
          font-size: 14px;
          color: #5a4a3a;
          margin-bottom: 5px;
          text-transform: uppercase;
          letter-spacing: 1px;
          font-family: 'Cormorant Garamond', serif;
        }

        .result-value {
          font-size: 1.3rem;
          font-weight: 600;
          color: #2c1810;
          font-family: 'Cormorant Garamond', serif;
        }

        /* Conversion Table */
        .conversion-section {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(212, 175, 55, 0.2);
          border-radius: 20px;
          padding: 40px 35px;
          margin-bottom: 50px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
        }

        .tab-buttons {
          display: flex;
          gap: 15px;
          margin-bottom: 20px;
          justify-content: center;
          flex-wrap: wrap;
        }

        .tab-button {
          padding: 12px 20px;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          color: #2c1810;
          border: 1px solid rgba(212, 175, 55, 0.2);
          border-radius: 15px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.3s ease;
          font-family: 'Cormorant Garamond', serif;
        }

        .tab-button.active {
          background: rgba(212, 175, 55, 0.2);
          color: #2c1810;
          border-color: rgba(212, 175, 55, 0.4);
          box-shadow: 0 4px 16px rgba(212, 175, 55, 0.2);
        }

        .tab-button:hover {
          background: rgba(212, 175, 55, 0.1);
          transform: translateY(-2px);
        }

        .conversion-table {
          width: 100%;
          border-collapse: collapse;
          text-align: center;
          overflow-x: auto;
        }

        .table-header {
          padding: 12px;
          color: #2c1810;
          font-weight: 600;
          font-size: 14px;
          border-bottom: 1px solid rgba(212, 175, 55, 0.2);
          min-width: 60px;
          transition: all 0.3s ease;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          font-family: 'Cormorant Garamond', serif;
        }

        .table-header.active {
          background: rgba(212, 175, 55, 0.1);
        }

        .table-cell {
          padding: 15px;
          border-bottom: 1px solid rgba(212, 175, 55, 0.1);
          color: #5a4a3a;
          font-weight: 400;
          transition: all 0.3s ease;
          font-family: 'Cormorant Garamond', serif;
        }

        .table-cell.active {
          background: rgba(212, 175, 55, 0.1);
          font-weight: 600;
        }

        .table-row:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        /* Measurement Guide */
        .measurement-section {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(212, 175, 55, 0.2);
          border-radius: 20px;
          padding: 40px 35px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
        }

        .measurement-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 35px;
          align-items: center;
          margin-top: 35px;
        }

        .measurement-steps {
          color: #2c1810;
          line-height: 1.8;
          font-size: 16px;
          font-family: 'Cormorant Garamond', serif;
        }

        .measurement-steps ol {
          padding-left: 20px;
        }

        .measurement-steps li {
          margin-bottom: 12px;
          transition: all 0.3s ease;
        }

        .measurement-steps li:hover {
          transform: translateX(5px);
          color: #d4af37;
        }

        .measurement-tips {
          margin-top: 25px;
        }

        .tip-text {
          color: #5a4a3a;
          font-style: italic;
          font-size: 15px;
          margin-bottom: 10px;
          font-family: 'Cormorant Garamond', serif;
        }

        .measurement-illustration {
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(212, 175, 55, 0.2);
          border-radius: 20px;
          padding: 30px;
          text-align: center;
          position: relative;
          overflow: hidden;
          min-height: 300px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
        }

        .illustration-svg {
          width: 100%;
          height: auto;
        }

        .illustration-text {
          color: #2c1810;
          font-size: 14px;
          margin-top: 20px;
          font-family: 'Cormorant Garamond', serif;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .hero-title {
            font-size: 2.5rem;
          }

          .hero-subtitle {
            font-size: 1.1rem;
          }

          .section-title {
            font-size: 2rem;
          }

          .calculator-section,
          .conversion-section,
          .measurement-section {
            padding: 30px 25px;
          }

          .tab-buttons {
            gap: 10px;
          }

          .tab-button {
            padding: 10px 16px;
            font-size: 13px;
          }

          .measurement-grid {
            grid-template-columns: 1fr;
            gap: 30px;
          }
        }

        @media (max-width: 480px) {
          .hero-title {
            font-size: 2rem;
            letter-spacing: 1px;
          }

          .hero-subtitle {
            font-size: 1rem;
          }

          .result-grid {
            gap: 20px;
          }

          .table-header,
          .table-cell {
            padding: 10px 8px;
            font-size: 13px;
          }
        }
      `}</style>

      <div className="size-guide-container">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="hero-section"
        >
          <div className="hero-overlay" />
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hero-content"
          >
            <motion.h1
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="hero-title"
            >
              Guide de Tailles
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="hero-subtitle"
            >
              Trouvez la taille parfaite pour vos babouches artisanales
            </motion.p>
          </motion.div>
        </motion.div>

        {/* Main Content */}
        <div className="main-content">
          {/* Size Calculator */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
            className="calculator-section"
          >
            <motion.h2 variants={itemVariants} className="section-title">
              Calculateur de Pointure
            </motion.h2>

            <motion.div
              variants={containerVariants}
              className="calculator-form"
            >
              <motion.div variants={itemVariants} className="input-group">
                <label htmlFor="footLength" className="input-label">
                  Longueur de votre pied (en cm) :
                </label>
                <motion.input
                  whileFocus={{
                    borderColor: "#8b7d6b",
                    backgroundColor: "rgba(212, 201, 188, 0.2)",
                    scale: 1.01,
                  }}
                  type="number"
                  id="footLength"
                  value={footLength}
                  onChange={(e) => setFootLength(e.target.value)}
                  className="size-input"
                  placeholder="Mesurez du talon à l'orteil le plus long"
                />
              </motion.div>

              <motion.button
                variants={itemVariants}
                whileHover={{
                  scale: 1.02,
                  boxShadow: "0 5px 15px rgba(212, 175, 55, 0.2)",
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                }}
                whileTap={{ scale: 0.98 }}
                onClick={calculateSize}
                className="calculate-btn"
              >
                Calculer ma taille
              </motion.button>

              {calculatedSize && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    transition: { type: "spring", stiffness: 100 },
                  }}
                  className="result-card"
                >
                  <motion.h3
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="result-title"
                  >
                    Votre taille recommandée :
                  </motion.h3>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ staggerChildren: 0.1, delayChildren: 0.3 }}
                    className="result-grid"
                  >
                    {Object.keys(sizeConversion).map((system) => (
                      <motion.div
                        key={system}
                        initial={{ y: 10, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.3 }}
                        className="result-item"
                      >
                        <p className="result-label">{system}</p>
                        <p className="result-value">{calculatedSize[system]}</p>
                      </motion.div>
                    ))}
                  </motion.div>
                </motion.div>
              )}
            </motion.div>
          </motion.div>

          {/* Size Conversion Table */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
            className="conversion-section"
          >
            <motion.h2 variants={itemVariants} className="section-title">
              Tableau de Conversion
            </motion.h2>

            <motion.div
              variants={itemVariants}
              style={{ overflowX: "auto", paddingBottom: "10px" }}
            >
              <motion.div variants={containerVariants} className="tab-buttons">
                {Object.keys(sizeConversion).map((system) => (
                  <motion.button
                    key={system}
                    variants={itemVariants}
                    whileHover={{
                      backgroundColor: "rgba(58, 50, 38, 0.05)",
                      scale: 1.05,
                    }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setActiveTab(system)}
                    className={`tab-button ${
                      activeTab === system ? "active" : ""
                    }`}
                  >
                    {system}
                  </motion.button>
                ))}
              </motion.div>

              <motion.table
                variants={itemVariants}
                className="conversion-table"
              >
                <thead>
                  <tr>
                    {Object.keys(sizeConversion).map((system) => (
                      <motion.th
                        key={system}
                        whileHover={{
                          backgroundColor: "rgba(58, 50, 38, 0.08)",
                        }}
                        className={`table-header ${
                          activeTab === system ? "active" : ""
                        }`}
                      >
                        {system}
                      </motion.th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sizeConversion.EU.map((_, index) => (
                    <motion.tr
                      key={index}
                      whileHover={{
                        backgroundColor: "rgba(250, 247, 243, 0.8)",
                      }}
                      transition={{ duration: 0.2 }}
                      className="table-row"
                    >
                      {Object.keys(sizeConversion).map((system) => (
                        <motion.td
                          key={system}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.3, delay: index * 0.05 }}
                          className={`table-cell ${
                            activeTab === system ? "active" : ""
                          }`}
                        >
                          {sizeConversion[system][index]}
                        </motion.td>
                      ))}
                    </motion.tr>
                  ))}
                </tbody>
              </motion.table>
            </motion.div>
          </motion.div>

          {/* Measurement Guide */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
            className="measurement-section"
          >
            <motion.h2 variants={itemVariants} className="section-title">
              Comment Mesurer Votre Pied
            </motion.h2>

            <motion.div
              variants={containerVariants}
              className="measurement-grid"
            >
              <motion.div variants={itemVariants}>
                <motion.ol
                  variants={containerVariants}
                  className="measurement-steps"
                >
                  {[
                    "Placez une feuille de papier contre un mur",
                    "Positionnez votre pied talon contre le mur",
                    "Marquez l'extrémité de votre orteil le plus long",
                    "Mesurez la distance entre le bord de la feuille et votre marque",
                    "Répétez pour l'autre pied et prenez la mesure la plus grande",
                  ].map((item, index) => (
                    <motion.li
                      key={index}
                      variants={itemVariants}
                      whileHover={{ x: 5 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      {item}
                    </motion.li>
                  ))}
                </motion.ol>

                <motion.div
                  variants={itemVariants}
                  className="measurement-tips"
                >
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="tip-text"
                  >
                    <strong>Conseil :</strong> Mesurez en fin de journée lorsque
                    vos pieds sont légèrement gonflés.
                  </motion.p>
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="tip-text"
                  >
                    <strong>Précision :</strong> Utilisez une règle rigide et
                    mesurez pieds nus pour plus d'exactitude.
                  </motion.p>
                </motion.div>
              </motion.div>

              <motion.div
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                className="measurement-illustration"
              >
                {/* Illustration SVG animée */}
                <motion.svg
                  viewBox="0 0 400 200"
                  className="illustration-svg"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  {/* Mur */}
                  <motion.rect
                    x="10"
                    y="50"
                    width="5"
                    height="150"
                    fill="#d4c9bc"
                    initial={{ height: 0 }}
                    animate={{ height: 150 }}
                    transition={{ duration: 0.5 }}
                  />

                  {/* Feuille de papier */}
                  <motion.rect
                    x="15"
                    y="50"
                    width="180"
                    height="150"
                    fill="#f8f5f0"
                    stroke="#d4c9bc"
                    strokeWidth="1"
                    initial={{ width: 0 }}
                    animate={{ width: 180 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                  />

                  {/* Pied */}
                  <motion.path
                    d="M20 150 Q50 130 80 150 Q110 170 140 150 Q170 130 200 150"
                    fill="#f0e0d0"
                    stroke="#c0a090"
                    strokeWidth="2"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                  />

                  {/* Ligne de mesure */}
                  <motion.line
                    x1="15"
                    y1="50"
                    x2="15"
                    y2="200"
                    stroke="#8b7d6b"
                    strokeWidth="1"
                    strokeDasharray="5,5"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6 }}
                  />
                  <motion.line
                    x1="200"
                    y1="150"
                    x2="15"
                    y2="150"
                    stroke="#3a3226"
                    strokeWidth="2"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.5, delay: 0.7 }}
                  />

                  {/* Marqueurs */}
                  <motion.circle
                    cx="15"
                    cy="50"
                    r="3"
                    fill="#5a3921"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", delay: 0.8 }}
                  />
                  <motion.circle
                    cx="15"
                    cy="150"
                    r="3"
                    fill="#5a3921"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", delay: 0.9 }}
                  />
                  <motion.circle
                    cx="200"
                    cy="150"
                    r="3"
                    fill="#5a3921"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", delay: 1.0 }}
                  />

                  {/* Texte */}
                  <motion.text
                    x="25"
                    y="40"
                    fill="#5a3921"
                    fontSize="12"
                    fontFamily="'Cormorant Garamond', serif"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.1 }}
                  >
                    Mur
                  </motion.text>
                  <motion.text
                    x="220"
                    y="155"
                    fill="#5a3921"
                    fontSize="12"
                    fontFamily="'Cormorant Garamond', serif"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.2 }}
                  >
                    Orteil le plus long
                  </motion.text>
                  <motion.text
                    x="100"
                    y="170"
                    fill="#5a3921"
                    fontSize="12"
                    fontFamily="'Cormorant Garamond', serif"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.3 }}
                  >
                    Longueur du pied
                  </motion.text>
                  <motion.text
                    x="10"
                    y="100"
                    fill="#5a3921"
                    fontSize="12"
                    fontFamily="'Cormorant Garamond', serif"
                    transform="rotate(-90, 10, 100)"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.4 }}
                  >
                    Mesure en cm
                  </motion.text>
                </motion.svg>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.5 }}
                  className="illustration-text"
                >
                  Schéma de mesure du pied - talon contre le mur
                </motion.p>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>

        <Footer />
      </div>
    </>
  );
};

export default SizeGuide;
